// Same-origin client for the login-service (Go) /sso/* endpoints.
//
// This SPA is always served from the SAME origin as the /sso API: Nginx proxies
// /sso/* → 127.0.0.1:8790 in production, and the Vite dev server proxies /sso and
// /.well-known → http://localhost:8790 in development. So all paths here are relative.
//
// Every /sso POST must carry the "X-Requested-With: sso-client" header (the service's
// CSRF guard) and credentials so the HttpOnly icm_sso cookie rides along.

export interface SsoSession {
  accessToken: string; // RS256 identity token (apps exchange this for their own JWT)
  sub: string;
  email: string;
  displayName?: string;
  expiresIn: number;
}

const SSO_HEADERS = {
  "Content-Type": "application/json",
  "X-Requested-With": "sso-client",
};

async function ssoPost(path: string, body?: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    headers: SSO_HEADERS,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

/**
 * The silent check: does this browser already hold a live SSO session?
 * Returns the session on 200, null on 401 ({code:"NO_SESSION"}).
 * Any other failure (network, 5xx) is also null — the page just shows the form.
 */
export async function ssoSilentSession(): Promise<SsoSession | null> {
  try {
    const res = await ssoPost("/sso/token");
    if (!res.ok) return null;
    return (await res.json()) as SsoSession;
  } catch {
    return null;
  }
}

/** GCIP sign-in finished → create the SSO session (sets the icm_sso cookie). */
export async function ssoLogin(idToken: string): Promise<SsoSession> {
  let res: Response;
  try {
    res = await ssoPost("/sso/login", { idToken });
  } catch {
    throw new Error("Cannot reach the sign-in service. Please try again.");
  }
  const data = await res.json().catch(() => ({}) as Record<string, unknown>);
  if (!res.ok) {
    throw new Error((data as { message?: string }).message ?? "Sign-in failed. Please try again.");
  }
  return data as SsoSession;
}

/** Revoke this browser's SSO session. Failures are ignored — the form re-renders anyway. */
export async function ssoLogout(): Promise<void> {
  try {
    await ssoPost("/sso/logout");
  } catch {
    // ignore — worst case the cookie is still set and the next visit silently signs in
  }
}
